import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import IdentifyBox, { Identity } from './IdentifyBox';
import { guidedText } from './guidedText';

// Fix orders at a station (Fernando 2.11): "I want to go to the PP station not in use at the moment and fix those.
// That means I need to be able to select those orders and click Fix button that requires manager PIN or badge. That
// allows me to Print delivery notes for the selected orders" — and then pack them there: note, every piece, note
// again, label → the order is processed at once. The server decides which orders may be fixed (pp_guided.fixable);
// this shows its answer, lets the manager untick a note, and asks the badge or PIN. Opened from the station's Orders
// page with the ticked orders.

interface Row {
  order_id: string; order_number: string; tab: string | null; tab_label: string; ok: boolean; reason: string;
  has_label: boolean; label_printed: boolean; note_printed_at?: string | null; print_default: boolean;
}

export default function FixDialog({ ids, onClose, onStarted }: {
  ids: string[]; onClose: () => void; onStarted: () => void;
}) {
  const tx = guidedText('en');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [printer, setPrinter] = useState('');
  const [prints, setPrints] = useState<Set<string>>(new Set());
  const [err, setErr] = useState('');
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    authFetch(`${API}/pp2/fix/preview?ids=${encodeURIComponent(ids.join(','))}`).then(async r => {
      const d = await r.json().catch(() => ({} as any));
      if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`); setRows([]); return; }
      setRows(d.orders || []);
      setPrinter(d.printer || '');
      setPrints(new Set((d.orders || []).filter((o: Row) => o.ok && o.print_default).map((o: Row) => o.order_id)));
    }).catch(() => { setErr('No connection to the WMS'); setRows([]); });
  }, [ids]);

  const okRows = (rows || []).filter(r => r.ok);
  const start = async (id: Identity): Promise<string | null> => {
    const r = await authFetch(`${API}/pp2/fix/start`, { method: 'POST', body: JSON.stringify({
      order_ids: okRows.map(o => o.order_id), print_ids: okRows.filter(o => prints.has(o.order_id)).map(o => o.order_id), ...id }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) return typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`;
    setAsking(false);
    onStarted();
    return null;
  };
  const labelText = (o: Row) => !o.has_label ? 'no label — one is bought at the note scan'
    : o.label_printed ? 'label printed — it is in the bag' : 'label not printed yet — it prints at the note scan';

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-[#1a1d27] border border-gray-700 p-6 space-y-4 text-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="text-2xl font-black text-white">Fix {ids.length} order{ids.length === 1 ? '' : 's'}</div>
        <div className="text-sm text-gray-400">
          A manager packs them again here: the delivery note, every piece, the note again, the label — each order is
          processed as soon as its label is scanned. Orders that cannot be fixed are left out.
        </div>
        {rows === null && <div className="text-gray-400">Checking the orders…</div>}
        {rows && rows.length > 0 && (
          <div className="space-y-2">
            {rows.map(o => (
              <div key={o.order_id} className={`rounded-lg border px-3 py-2 ${o.ok ? 'border-gray-700' : 'border-red-900/60 bg-red-900/10'}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-white font-bold w-28">{o.order_number}</span>
                  <span className="text-gray-400">{o.tab_label}</span>
                  {o.ok ? (
                    <label className="ml-auto flex items-center gap-2 text-sm">
                      <input type="checkbox" className="w-4 h-4" checked={prints.has(o.order_id)}
                        onChange={e => setPrints(prev => { const n = new Set(prev); if (e.target.checked) n.add(o.order_id); else n.delete(o.order_id); return n; })} />
                      Print a new delivery note
                    </label>
                  ) : <span className="ml-auto text-red-300 text-sm">{o.reason}</span>}
                </div>
                {o.ok && <div className="text-xs text-gray-500 mt-1">{labelText(o)}</div>}
              </div>
            ))}
          </div>
        )}
        {prints.size > 0 && (
          <div className="text-sm text-amber-200">
            {printer ? <>The {prints.size} delivery note{prints.size === 1 ? '' : 's'} print on <b>{printer}</b> — fetch them there.</>
                     : <>No A4 printer is set up (Settings → Printing) — the notes cannot be printed.</>}
          </div>
        )}
        {err && <div className="text-red-400 text-sm">{err}</div>}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-5 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-white">{tx.cancel}</button>
          <button disabled={okRows.length === 0 || (prints.size > 0 && !printer)} onClick={() => { setErr(''); setAsking(true); }}
            className="px-6 py-3 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white text-lg font-bold">
            Fix {okRows.length} — a manager's badge or PIN
          </button>
        </div>
      </div>
      {asking && (
        <IdentifyBox title={tx.fixManagerTitle} hint={tx.badgeOrPin} cancelLabel={tx.cancel} busyText={tx.identifyBusy}
          onSubmit={start} onCancel={() => setAsking(false)} />
      )}
    </div>
  );
}
